import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { stripHtmlTags } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

const TRUNCATE_LENGTH = 80

const useStyles = makeStyles((theme: Theme) => ({
    cell: {
        cursor: 'pointer',
        fontSize: pxToRem(14),
        fontFamily: 'Roboto',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    emptyValue: {
        fontSize: pxToRem(14),
        color: '#bbb',
    },
    editLinkIcon: {
        fontSize: pxToRem(14),
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: theme.spacing(1),
        '& h2': {
            fontSize: pxToRem(16),
            fontWeight: 600,
        },
    },
    dialogContent: {
        minWidth: 480,
        maxWidth: 640,
        paddingTop: 0,
        paddingBottom: theme.spacing(3),
    },
    descriptionText: {
        fontSize: pxToRem(14),
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        color: '#333',
    },
    editLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        fontSize: pxToRem(13),
        color: '#1976d2',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    closeButton: {
        padding: theme.spacing(0.5),
    },
}))

interface DescriptionPreviewCellProps {
    value: string
    row: { original: { listingId?: string } }
}

/**
 * Truncates product description to 80 chars in the table; clicking opens a read-only preview
 * dialog with a link to the product card.
 *
 * Used for column: description.
 */
const DescriptionPreviewCell = ({
    value,
    row,
}: DescriptionPreviewCellProps) => {
    const classes = useStyles()
    const [open, setOpen] = useState(false)

    const listingId = row?.original?.listingId
    const productDetailPath = listingId
        ? `/bpm/products/product/listing/${listingId}`
        : `/bpm/products/all`

    const handleOpen = useCallback(() => {
        if (value) setOpen(true)
    }, [value])

    const handleClose = useCallback(() => setOpen(false), [])

    if (!value) {
        return <Typography className={classes.emptyValue}>{'—'}</Typography>
    }

    const plain = stripHtmlTags(value)

    if (!plain) {
        return <Typography className={classes.emptyValue}>{'—'}</Typography>
    }

    const truncated =
        plain.length > TRUNCATE_LENGTH
            ? `${plain.slice(0, TRUNCATE_LENGTH)}\u2026`
            : plain

    return (
        <>
            <TooltipEllipsis text={truncated}>
                <Typography
                    className={classes.cell}
                    noWrap
                    onClick={handleOpen}
                >
                    {truncated}
                </Typography>
            </TooltipEllipsis>

            <Dialog open={open} onClose={handleClose} maxWidth="sm">
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h2">Product Description</Typography>
                    <IconButton
                        className={classes.closeButton}
                        onClick={handleClose}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.descriptionText}>
                        {plain}
                    </Typography>
                    <Link
                        to={{
                            pathname: productDetailPath,
                            search: '?showBackButton=false',
                        }}
                        target="_blank"
                        className={classes.editLink}
                    >
                        <OpenInNewIcon className={classes.editLinkIcon} />
                        {'Edit on product card'}
                    </Link>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default DescriptionPreviewCell
